import React, { useEffect } from 'react';
import {
  type PolicyWithChange,
  getPolicyState,
} from '../utils/policyProposalUtils';
import { MoreHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { PolicyMenuOptions } from './policy-menu-options';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getTransactionTypeLabel, formatAddress } from '../utils';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';

import { cn } from '@/lib/utils';
import {
  ReorderableList,
  SortableItem,
} from '@/components/shared/reorderable-list';

interface PoliciesTableProps {
  policies: PolicyWithChange[];
  onRowClick: (policy: PolicyWithChange) => void;
  showEditStateBadges?: boolean;
  onEditPolicy?: (policy: PolicyWithChange) => void;
  onReviewChanges?: (policy: PolicyWithChange) => void;
  onRemovePolicy?: (policy: PolicyWithChange) => void;
  onUndoProposal?: (policy: PolicyWithChange) => void;
  disableEditAndRemove?: boolean;
  emptyMessage?: React.ReactNode;
  isReorderable?: boolean;
  isReordering?: boolean;
  onReorder?: (activeId: string, overId: string) => void | Promise<void>;
}

interface PolicyRowProps {
  policy: PolicyWithChange;
  onRowClick: (policy: PolicyWithChange) => void;
  showEditStateBadges: boolean;
  renderActionBadge: (action: string) => React.ReactElement;
  renderConditionSummary: (policy: PolicyWithChange) => string;
  renderAccountsDisplay: (policy: PolicyWithChange) => React.ReactElement;
  renderEditStateBadge: (policy: PolicyWithChange) => React.ReactElement | null;
  animationState?: 'entering' | 'exiting' | undefined;
  onEditPolicy?: ((policy: PolicyWithChange) => void) | undefined;
  onReviewChanges?: ((policy: PolicyWithChange) => void) | undefined;
  onRemovePolicy?: ((policy: PolicyWithChange) => void) | undefined;
  onUndoProposal?: ((policy: PolicyWithChange) => void) | undefined;
  disableEditAndRemove?: boolean;
  dragHandle?: React.ReactNode;
  rowRef?: React.Ref<HTMLTableRowElement>;
  rowStyle?: React.CSSProperties;
}

const PolicyRow = React.memo(function PolicyRow({
  policy,
  onRowClick,
  showEditStateBadges,
  renderActionBadge,
  renderConditionSummary,
  renderAccountsDisplay,
  renderEditStateBadge,
  animationState,
  onEditPolicy,
  onReviewChanges,
  onRemovePolicy,
  onUndoProposal,
  disableEditAndRemove,
  dragHandle,
  rowRef,
  rowStyle,
}: PolicyRowProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);

  // Check if there are any menu options to show
  const policyState = getPolicyState(policy);
  const canReviewChanges = policyState === 'modified';
  const hasChanges = policyState !== 'original';
  const canUndo = hasChanges && !!onUndoProposal;
  const canEdit = !hasChanges && !disableEditAndRemove;
  const hasMenuOptions =
    (canReviewChanges && !!onReviewChanges) || canUndo || canEdit;

  // Handle transition-based animations
  React.useEffect(() => {
    if (animationState) {
      // Small delay to ensure the base class is applied first
      const timer = setTimeout(() => {
        setIsActive(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsActive(false);
    }
  }, [animationState, policy.name]);

  // Determine animation classes
  const getAnimationClasses = () => {
    if (animationState === 'entering') {
      return `table-row-enter ${isActive ? 'table-row-enter-active' : ''}`;
    }
    if (animationState === 'exiting') {
      return `table-row-exit ${isActive ? 'table-row-exit-active' : ''}`;
    }
    return '';
  };

  const tableRow = (
    <StyledTableRow
      ref={rowRef}
      style={rowStyle}
      onClick={() => onRowClick(policy)}
      className={cn('cursor-pointer', getAnimationClasses())}
    >
      {dragHandle ? (
        <StyledTableCell className="w-10 px-1 first:pl-1 py-2 text-center">
          {dragHandle}
        </StyledTableCell>
      ) : null}
      <StyledTableCell className={dragHandle ? 'pl-0' : undefined}>
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-base">{policy.name}</div>
          <div className="text-xs text-muted-foreground font-normal">
            {policy.description}
          </div>
        </div>
      </StyledTableCell>
      <StyledTableCell>{renderConditionSummary(policy)}</StyledTableCell>
      <StyledTableCell>
        <div className="flex flex-col gap-1">
          <div className="font-medium text-sm">
            {renderActionBadge(policy.type)}
          </div>
          {/* TODO: Show group info when backend supports it */}
        </div>
      </StyledTableCell>
      <StyledTableCell>{renderAccountsDisplay(policy)}</StyledTableCell>
      {showEditStateBadges && (
        <StyledTableCell>{renderEditStateBadge(policy)}</StyledTableCell>
      )}

      {/* Kebab Menu Column - only show when in edit mode AND there are menu options */}
      {showEditStateBadges && (
        <StyledTableCell className="text-center">
          {hasMenuOptions && (
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 data-[state=open]:bg-muted"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 z-[9999]">
                <PolicyMenuOptions
                  policy={policy}
                  onEditPolicy={onEditPolicy}
                  onReviewChanges={onReviewChanges}
                  onRemovePolicy={onRemovePolicy}
                  onUndoProposal={onUndoProposal}
                  onActionComplete={() => setDropdownOpen(false)}
                  disableEditAndRemove={disableEditAndRemove || false}
                  MenuItem={DropdownMenuItem}
                  MenuSeparator={DropdownMenuSeparator}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </StyledTableCell>
      )}
    </StyledTableRow>
  );

  // Wrap with ContextMenu for right-click functionality when in edit mode AND there are menu options
  if (showEditStateBadges && hasMenuOptions) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>{tableRow}</ContextMenuTrigger>
        <ContextMenuContent className="w-48 z-[9999]">
          <PolicyMenuOptions
            policy={policy}
            onEditPolicy={onEditPolicy}
            onReviewChanges={onReviewChanges}
            onRemovePolicy={onRemovePolicy}
            onUndoProposal={onUndoProposal}
            onActionComplete={() => {}} // ContextMenu closes automatically
            disableEditAndRemove={disableEditAndRemove || false}
            MenuItem={ContextMenuItem}
            MenuSeparator={ContextMenuSeparator}
          />
        </ContextMenuContent>
      </ContextMenu>
    );
  }

  return tableRow;
});

export function PoliciesTable({
  policies,
  onRowClick,
  showEditStateBadges = false,
  onEditPolicy,
  onReviewChanges,
  onRemovePolicy,
  onUndoProposal,
  disableEditAndRemove = false,
  emptyMessage,
  isReorderable = false,
  isReordering = false,
  onReorder,
}: PoliciesTableProps) {
  const [confirmDeletePolicy, setConfirmDeletePolicy] =
    React.useState<PolicyWithChange | null>(null);
  const [animatingPolicies, setAnimatingPolicies] = React.useState<{
    [key: string]: 'entering' | 'exiting';
  }>({});
  const [displayPolicies, setDisplayPolicies] =
    React.useState<PolicyWithChange[]>(policies);
  const previousPoliciesRef = React.useRef<PolicyWithChange[]>(policies);

  // Track policy additions and removals for animations
  useEffect(() => {
    const previousIds = new Set(previousPoliciesRef.current.map((p) => p.id));
    const currentIds = new Set(policies.map((p) => p.id));

    // Find newly added policies
    const addedIds = policies
      .filter((p) => !previousIds.has(p.id))
      .map((p) => p.id);

    // Find removed policies
    const removedIds = previousPoliciesRef.current
      .filter((p) => !currentIds.has(p.id))
      .map((p) => p.id);

    if (addedIds.length > 0) {
      // Mark new policies as entering
      setAnimatingPolicies((prev) => {
        const newAnimatingStates = { ...prev };
        addedIds.forEach((id) => {
          newAnimatingStates[id] = 'entering';
        });
        return newAnimatingStates;
      });

      // Update display policies to include new ones
      setDisplayPolicies(policies);

      // Remove entering state after animation completes
      setTimeout(() => {
        setAnimatingPolicies((prev) => {
          const updated = { ...prev };
          addedIds.forEach((id) => delete updated[id]);
          return updated;
        });
      }, 300);
    }

    if (removedIds.length > 0) {
      // Mark removed policies as exiting
      setAnimatingPolicies((prev) => {
        const newAnimatingStates = { ...prev };
        removedIds.forEach((id) => {
          newAnimatingStates[id] = 'exiting';
        });
        return newAnimatingStates;
      });

      // Keep previous policies for exit animation, then update after animation
      setDisplayPolicies((prev) => [...prev]);

      // Actually remove policies after animation completes
      setTimeout(() => {
        setDisplayPolicies(policies);
        setAnimatingPolicies((prev) => {
          const updated = { ...prev };
          removedIds.forEach((id) => delete updated[id]);
          return updated;
        });
      }, 300);
    } else {
      // If no removal animations, just update display policies
      setDisplayPolicies(policies);
    }

    // Update ref for next comparison
    previousPoliciesRef.current = policies;
  }, [policies]);

  const renderActionBadge = React.useCallback((action: string) => {
    // Convert MANUAL_APPROVAL -> Manual Approval, AUTO_APPROVAL -> Auto Approval
    const label = action
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
    return <div>{label}</div>;
  }, []);

  const renderConditionSummary = React.useCallback(
    (policy: PolicyWithChange) => {
      return getTransactionTypeLabel(policy.transactionType);
    },
    []
  );

  const renderAccountsDisplay = React.useCallback(
    (policy: PolicyWithChange) => {
      if (!policy.accounts || policy.accounts.length === 0) {
        return <div className="text-muted-foreground text-sm">Any account</div>;
      }

      if (policy.accounts.length === 1 && policy.accounts[0]) {
        const account = policy.accounts[0];
        return (
          <div className="flex flex-col text-sm">
            <span className="font-medium">{account.name}</span>
            {account.address && (
              <span className="text-xs text-muted-foreground font-mono">
                {formatAddress(account.address)}
              </span>
            )}
          </div>
        );
      }

      const firstAccount = policy.accounts[0];
      if (!firstAccount) {
        return <div className="text-muted-foreground text-sm">Any account</div>;
      }

      return (
        <div className="flex flex-col gap-1">
          <div className="flex flex-col text-sm">
            <span className="font-medium">{firstAccount.name}</span>
            {firstAccount.address && (
              <span className="text-xs text-muted-foreground font-mono">
                {formatAddress(firstAccount.address)}
              </span>
            )}
          </div>
          {policy.accounts.length > 1 && (
            <div className="text-xs text-muted-foreground">
              +{policy.accounts.length - 1} more account
              {policy.accounts.length > 2 ? 's' : ''}
            </div>
          )}
        </div>
      );
    },
    []
  );

  const renderEditStateBadge = React.useCallback((policy: PolicyWithChange) => {
    const state = getPolicyState(policy);

    if (state === 'new') {
      return (
        <Badge
          variant="secondary"
          className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
        >
          New
        </Badge>
      );
    }

    if (state === 'modified') {
      return (
        <Badge
          variant="secondary"
          className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
        >
          Modified
        </Badge>
      );
    }

    if (state === 'deleted') {
      return (
        <Badge
          variant="secondary"
          className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
        >
          Deleted
        </Badge>
      );
    }

    if (state === 'original') {
      return (
        <Badge
          variant="secondary"
          className="bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
        >
          Unmodified
        </Badge>
      );
    }

    return null;
  }, []);

  const handleRemovePolicyClick = React.useCallback(
    (policy: PolicyWithChange) => {
      setConfirmDeletePolicy(policy);
    },
    []
  );

  const handleConfirmRemovePolicy = () => {
    if (confirmDeletePolicy && onRemovePolicy) {
      onRemovePolicy(confirmDeletePolicy);
      setConfirmDeletePolicy(null);
    }
  };

  const handleCancelRemovePolicy = () => {
    setConfirmDeletePolicy(null);
  };

  // Reordering preserves the caller's current order; otherwise use the
  // animation-aware display collection.
  const renderedPolicies = isReorderable ? policies : displayPolicies;
  const tableContent =
    renderedPolicies.length === 0 && emptyMessage ? (
      <div className="h-48 flex items-center justify-center text-slate-500">
        {emptyMessage}
      </div>
    ) : (
      <StyledTableWrapper>
        <ReorderableList
          ids={renderedPolicies.map((policy) => policy.id)}
          disabled={!isReorderable || isReordering || !onReorder}
          onReorder={onReorder ?? (() => {})}
        >
          <StyledTable>
            <StyledTableHeader>
              <tr>
                {isReorderable && (
                  <StyledTableHead className="w-10 px-1 first:pl-1" />
                )}
                <StyledTableHead className={isReorderable ? 'pl-0' : undefined}>
                  Name
                </StyledTableHead>
                <StyledTableHead>Conditions</StyledTableHead>
                <StyledTableHead>Type</StyledTableHead>
                <StyledTableHead>Accounts</StyledTableHead>
                {showEditStateBadges && (
                  <StyledTableHead>Status</StyledTableHead>
                )}
                {showEditStateBadges && <StyledTableHead></StyledTableHead>}
              </tr>
            </StyledTableHeader>
            <StyledTableBody>
              {renderedPolicies.map((policy) => {
                const animationState = animatingPolicies[policy.id];
                return (
                  <SortableItem
                    key={policy.id}
                    id={policy.id}
                    label={policy.name}
                    disabled={!isReorderable || isReordering || !onReorder}
                  >
                    {({ setNodeRef, style, dragHandle }) => (
                      <PolicyRow
                        policy={policy}
                        onRowClick={onRowClick}
                        showEditStateBadges={showEditStateBadges}
                        renderActionBadge={renderActionBadge}
                        renderConditionSummary={renderConditionSummary}
                        renderAccountsDisplay={renderAccountsDisplay}
                        renderEditStateBadge={renderEditStateBadge}
                        onEditPolicy={onEditPolicy}
                        onReviewChanges={onReviewChanges}
                        onRemovePolicy={handleRemovePolicyClick}
                        onUndoProposal={onUndoProposal}
                        disableEditAndRemove={disableEditAndRemove}
                        dragHandle={isReorderable ? dragHandle : undefined}
                        rowRef={setNodeRef}
                        rowStyle={style}
                        {...(animationState ? { animationState } : {})}
                      />
                    )}
                  </SortableItem>
                );
              })}
            </StyledTableBody>
          </StyledTable>
        </ReorderableList>
      </StyledTableWrapper>
    );

  return (
    <>
      {tableContent}

      {/* Confirmation Dialog for Removing Policy */}
      <Dialog
        open={!!confirmDeletePolicy}
        onOpenChange={() => setConfirmDeletePolicy(null)}
      >
        <DialogContent
          className="z-[9999]"
          dialogOverlayProps={{ className: 'z-[9999]' }}
        >
          <DialogHeader>
            <DialogTitle>Remove Policy</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the policy "
              {confirmDeletePolicy?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelRemovePolicy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemovePolicy}>
              Remove Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
